import React from 'react';
import { useTranslation } from 'react-i18next';
import { Vector3 } from 'three';

import { roundToDecimals } from '../../../../../../../utils/math/number';
import { isDefined } from '../../../../../../../utils/variables';
import { CoordinateManager } from '../../../../../../../viewer/coordinate-manager';
import { ActionName, DesignTool } from '../../../../../../../viewer/tools/design-tool';
import { createPointAtPosition } from '../../../../../../../viewer/tools/utils/context-menu/design-tool/commands/point/create-from-dialog';
import { Coordinates, Point3d } from '../../../../../../../viewer/utilities/math-helpers';
import { ProjectSpatialReferenceContext } from '../../../../../../state/project-reference-state';
import { ConfirmationDialog } from '../../../../../confirmation-dialog/confirmation-dialog';
import { InfoBox } from '../../../../../info-box/info-box';
import { LabelledContainer } from '../../../../../labelled-container/labelled-container';
import { Stack } from '../../../../../stack/stack';
import { PositionInput } from '../../../components/position-input';
import { CoordinateValidationInput } from '../../reference-point-transform/coordinate-validation-input';

type Props = {
  tool: DesignTool;
};

/**
 * Converts a world position to a project coordinate, where x is northing, y is easting and z is height
 * to match the order of the inputs.
 */
const toNorthingEastingHeight = (position: Vector3): Point3d => {
  const coordinates = CoordinateManager.getInstance().localToProjectCoords(position);
  return { x: coordinates.northing, y: coordinates.easting, z: coordinates.height ?? 0 };
};

const formatCoordinate = (point: Point3d) =>
  `N ${roundToDecimals(point.x, 3)}   E ${roundToDecimals(point.y, 3)}   H ${roundToDecimals(point.z, 3)}`;

/**
 * Tells what is created, the next point of the line being created or a separate point.
 */
const CreatedPointInfo = ({ tool, separatePointText }: Props & { separatePointText?: string }) => {
  const { t } = useTranslation();
  const text =
    tool.actionInProgress?.actionName === ActionName.CreateLine
      ? t('createPointDialog.continuesLine', { ns: 'skyviewDesign' })
      : separatePointText;

  if (!isDefined(text)) {
    return null;
  }

  return (
    <InfoBox color="blue" leftIcon={{ icon: 'info' }}>
      {text}
    </InfoBox>
  );
};

export const CreatePointByCoordinateDialog = ({ tool }: Props) => {
  const { t } = useTranslation();
  const { getCoordinateSystemOrDefault } = React.useContext(ProjectSpatialReferenceContext);
  const [coordinateSystem] = React.useState(getCoordinateSystemOrDefault);
  const [isValid, setIsValid] = React.useState(true);
  const [defaultValue] = React.useState<Point3d>(() =>
    isDefined(tool.pointDialogOrigin)
      ? toNorthingEastingHeight(tool.pointDialogOrigin)
      : // The last used coordinate is stored with x as easting and y as northing.
        {
          x: tool.snapValues.position.y,
          y: tool.snapValues.position.x,
          z: tool.snapValues.position.z,
        },
  );
  const [coordinate, setCoordinate] = React.useState<Coordinates>({
    northing: defaultValue.x,
    easting: defaultValue.y,
    height: defaultValue.z,
  });

  return (
    <ConfirmationDialog
      confirmationText={t('createPointDialog.create', { ns: 'skyviewDesign' })}
      confirmDisabled={!isValid}
      title={t('createPointDialog.coordinate.title', { ns: 'skyviewDesign' })}
      onCancel={() => tool.onDialogClose()}
      onConfirm={async () => {
        tool.onSnapValuesChanged({
          position: new Vector3(coordinate.easting, coordinate.northing, coordinate.height),
        });

        await createPointAtPosition(
          tool,
          CoordinateManager.getInstance().projectCoordsToLocal(coordinate),
        );

        tool.onDialogClose();
      }}
    >
      <Stack spacing={1}>
        <CoordinateValidationInput
          coordinateSystem={coordinateSystem}
          defaultValue={defaultValue}
          leftBorderColor="green"
          showHeight={true}
          onValidate={setIsValid}
          onValueChanged={setCoordinate}
        />
        <CreatedPointInfo tool={tool} />
      </Stack>
    </ConfirmationDialog>
  );
};

export const CreatePointByOffsetDialog = ({ tool }: Props) => {
  const { t } = useTranslation();
  // The last used offset is stored with x as easting and y as northing.
  const [offset, setOffset] = React.useState<Point3d>({
    x: tool.snapValues.xyzOffset.y,
    y: tool.snapValues.xyzOffset.x,
    z: tool.snapValues.xyzOffset.z,
  });

  const origin = tool.pointDialogOrigin;

  if (!isDefined(origin)) {
    return null;
  }

  const offsetVector = new Vector3(offset.y, offset.x, offset.z);
  const newPosition = origin.clone().add(offsetVector);

  return (
    <ConfirmationDialog
      confirmationText={t('createPointDialog.create', { ns: 'skyviewDesign' })}
      title={t('createPointDialog.offset.title', { ns: 'skyviewDesign' })}
      onCancel={() => tool.onDialogClose()}
      onConfirm={async () => {
        tool.onSnapValuesChanged({ xyzOffset: offsetVector });
        await createPointAtPosition(tool, newPosition);
        tool.onDialogClose();
      }}
    >
      <Stack spacing={1}>
        <LabelledContainer text={t('createPointDialog.offset.origin', { ns: 'skyviewDesign' })}>
          <span>{formatCoordinate(toNorthingEastingHeight(origin))}</span>
        </LabelledContainer>
        <PositionInput
          defaultValue={offset}
          display={[
            t('createPointDialog.offset.input.northing', { ns: 'skyviewDesign' }),
            t('createPointDialog.offset.input.easting', { ns: 'skyviewDesign' }),
            t('createPointDialog.offset.input.height', { ns: 'skyviewDesign' }),
          ]}
          onValueChanged={setOffset}
        />
        <LabelledContainer text={t('createPointDialog.offset.newPoint', { ns: 'skyviewDesign' })}>
          <span>{formatCoordinate(toNorthingEastingHeight(newPosition))}</span>
        </LabelledContainer>
        <CreatedPointInfo
          separatePointText={t('createPointDialog.offset.separatePoint', { ns: 'skyviewDesign' })}
          tool={tool}
        />
      </Stack>
    </ConfirmationDialog>
  );
};
